import React from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  BadgeDollarSign,
  BellRing,
  CheckCircle2,
  ClipboardCheck,
  Download,
  FileSearch,
  IdCard,
  ShieldCheck,
} from 'lucide-react'
import MarketingSiteHeader from '../marketing/MarketingSiteHeader'
import { PUBLIC_INQUIRY_PATH } from '../marketing/marketingNav'
import PublicFooter from '../ui/PublicFooter'
import {
  LandingFlowHeroImage,
} from '../marketing/SgezLandingVisuals'

const heroFlowItems = ['고객 접점', '진료정보분석', '설계의뢰', '계약관리', '만기·청구']

const featureCards = [
  {
    title: '모바일 명함과 개인홈페이지',
    desc: '고객이 상담을 남길 수 있는 개인 접점을 빠르게 만듭니다.',
    Icon: IdCard,
  },
  {
    title: '오늘의 업무브리핑',
    desc: '오늘 챙겨야 할 고객과 계약 업무를 매일 아침 알림톡으로 정리합니다.',
    Icon: BellRing,
  },
  {
    title: '진료정보분석',
    desc: '5년 진료정보를 분석해 예외질환과 보험사별 3NN 추천 근거를 보여줍니다.',
    Icon: FileSearch,
  },
  {
    title: '숨은보험금찾기',
    desc: '의료비와 실손 수령액을 비교해 추가 청구 가능성을 확인합니다.',
    Icon: BadgeDollarSign,
  },
  {
    title: '화재보험설계',
    desc: '지역 화재 통계와 업종 기준으로 필요한 특약을 추천합니다.',
    Icon: ShieldCheck,
  },
  {
    title: '설계의뢰와 계약관리',
    desc: '설계의뢰, 계약, 만기, 청구, 3종 확인서까지 같은 흐름에서 관리합니다.',
    Icon: ClipboardCheck,
  },
]

const effectStats = [
  ['0회', '설계매니저 연락처 찾기', '등록된 설계매니저에게 설계의뢰를 자동 발송합니다.'],
  ['1개', '고객 맞춤 통합 리포트', '진료정보, 추가 청구 가능성, 상담 포인트를 하나의 리포트로 정리합니다.'],
  ['5분', '고객상담 핵심정보 정리', '고객 현재 상황에 맞는 상담 핵심 정보를 빠르게 확인합니다.'],
  ['1번', '여러 보험사 간편청구', '여러 보험사 보험금청구를 한 번의 흐름으로 진행합니다.'],
]

const screenShowcases = [
  {
    no: '01',
    title: '오늘의 업무브리핑',
    desc: '출근 전 오늘 챙겨야 할 고객, 만기, 갱신, 설계의뢰 업무를 먼저 정리합니다.',
    chips: ['알림톡', '오늘 할 일', '만기·갱신'],
    Screen: BriefingScreen,
  },
  {
    no: '02',
    title: '진료정보분석',
    desc: '5년 진료정보를 상담 요약, 예외질환, 보험사별 3NN 추천 근거로 보여줍니다.',
    chips: ['최근 5년', '예외질환', '3NN 추천'],
    Screen: HealthSummaryScreen,
  },
  {
    no: '03',
    title: '숨은보험금찾기',
    desc: '의료비와 실손 수령액을 비교해 고객의 추가 청구 가능성을 정리합니다.',
    chips: ['의료비 비교', '실손 수령액', '추가 확인'],
    Screen: HiddenMoneyScreen,
  },
  {
    no: '04',
    title: '설계의뢰와 계약관리',
    desc: '등록된 설계매니저에게 의뢰를 보내고, 계약·만기·청구 후속 업무까지 이어갑니다.',
    chips: ['자동 의뢰', '계약관리', '만기·청구'],
    Screen: RequestManagementScreen,
  },
  {
    no: '05',
    title: '고객 접점',
    desc: '모바일 명함과 개인홈페이지, 랜딩페이지로 상담이 시작되는 입구를 만듭니다.',
    chips: ['모바일 명함', '개인홈페이지', '상담 접수'],
    Screen: CustomerTouchpointScreen,
  },
]

function PrimaryLink({ to, children, variant = 'dark' }) {
  const styles = {
    dark: 'bg-slate-950 text-white shadow-lg shadow-slate-950/18 hover:bg-brand-navy',
    blue: 'bg-white text-brand-navy shadow-lg shadow-blue-950/10 hover:bg-sky-50',
  }

  return (
    <Link
      to={to}
      className={`inline-flex min-h-[50px] items-center justify-center gap-2 rounded-md px-6 text-sm font-bold transition ${styles[variant]}`}
    >
      {children}
    </Link>
  )
}

function OutlineLink({ to, children, light = false }) {
  return (
    <Link
      to={to}
      className={`inline-flex min-h-[50px] items-center justify-center rounded-md border px-6 text-sm font-bold transition ${
        light
          ? 'border-white/45 bg-white/10 text-white hover:bg-white/20'
          : 'border-slate-300 bg-white text-slate-800 hover:border-sky-300 hover:bg-sky-50'
      }`}
    >
      {children}
    </Link>
  )
}

function MonitorFrame({ children }) {
  return (
    <div className="mx-auto w-full max-w-[30rem]">
      {children}
    </div>
  )
}

function BriefingScreen() {
  return (
    <div className="min-h-[330px]">
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <p className="text-xs font-bold text-brand-sky">TODAY</p>
          <h3 className="mt-2 text-2xl font-bold text-slate-950">오늘 할 일</h3>
        </div>
        <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-bold text-brand-navy">오전 8:30 발송</span>
      </div>
      <div className="mt-5 grid gap-3">
        {[
          ['계약 만기 확인', '3건', '오늘'],
          ['갱신 안내 연락', '5명', '이번 주'],
          ['설계의뢰 답변 확인', '2건', '대기'],
          ['청구 보완 요청', '1건', '확인'],
        ].map(([title, count, status]) => (
          <div key={title} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 rounded-md border border-slate-200 px-4 py-3">
            <span className="text-sm font-bold text-slate-800">{title}</span>
            <span className="text-sm font-bold text-brand-navy">{count}</span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-500">{status}</span>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-md bg-[#f5f8ff] p-4">
        <p className="text-sm font-bold text-slate-950">알림톡 요약</p>
        <p className="mt-2 text-sm font-medium leading-6 text-slate-600">
          오늘 우선 확인할 고객과 계약 업무를 자동으로 묶어 발송합니다.
        </p>
      </div>
    </div>
  )
}

function HealthSummaryScreen() {
  return (
    <div className="min-h-[330px]">
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <p className="text-xs font-bold text-brand-sky">진료정보분석</p>
          <h3 className="mt-2 text-2xl font-bold text-slate-950">요약정보</h3>
        </div>
        <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-bold text-brand-navy">최근 5년</span>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3">
        {[
          ['통원', '18건'],
          ['검사', '4건'],
          ['투약', '9건'],
        ].map(([label, value]) => (
          <div key={label} className="rounded-md bg-[#f5f8ff] p-3">
            <p className="text-xs font-bold text-slate-500">{label}</p>
            <p className="mt-2 text-xl font-bold text-slate-950">{value}</p>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-md border border-slate-200 p-4">
        <p className="text-sm font-bold text-slate-950">예외질환 자동분석</p>
        <div className="mt-3 grid gap-2">
          {['고지 확인 필요 항목 2건', '3NN 추천 후보 3개', '상담 메모 자동 요약'].map((item) => (
            <div key={item} className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <CheckCircle2 className="h-4 w-4 text-brand-sky" aria-hidden />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 grid gap-2">
        {['A보험사 추천 근거', 'B보험사 보완 확인', 'C보험사 후보 유지'].map((item, index) => (
          <div key={item} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-xs font-bold">
            <span className="text-slate-700">{item}</span>
            <span className={index === 0 ? 'text-brand-navy' : 'text-slate-500'}>{index === 0 ? '우선' : '검토'}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function HiddenMoneyScreen() {
  return (
    <div className="min-h-[330px]">
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <p className="text-xs font-bold text-brand-sky">숨은보험금찾기</p>
          <h3 className="mt-2 text-2xl font-bold text-slate-950">분석 결과</h3>
        </div>
        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">가능성 높음</span>
      </div>
      <div className="mt-5 rounded-md bg-[#f5f8ff] p-5">
        <p className="text-sm font-bold text-slate-600">추가 확인 예상 금액</p>
        <p className="mt-2 text-4xl font-bold text-brand-navy">460,000원</p>
        <div className="mt-5 h-3 overflow-hidden rounded-full bg-white">
          <div className="h-full w-[72%] rounded-full bg-gradient-to-r from-brand-navy to-brand-sky" />
        </div>
      </div>
      <div className="mt-5 grid gap-3">
        {[
          ['총 의료비', '1,240,000원'],
          ['실손 수령액', '780,000원'],
          ['추가 확인 대상', '3건'],
        ].map(([label, value]) => (
          <div key={label} className="flex items-center justify-between rounded-md border border-slate-200 px-4 py-3">
            <span className="text-sm font-medium text-slate-600">{label}</span>
            <span className="text-sm font-bold text-slate-950">{value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function RequestManagementScreen() {
  return (
    <div className="min-h-[330px]">
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <p className="text-xs font-bold text-brand-sky">설계의뢰</p>
          <h3 className="mt-2 text-2xl font-bold text-slate-950">진행 현황</h3>
        </div>
        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">자동 배정</span>
      </div>
      <div className="mt-5 grid grid-cols-4 gap-2">
        {['접수', '배정', '진행', '완료'].map((step, index) => (
          <div key={step} className="rounded-md bg-[#f5f8ff] px-3 py-3 text-center">
            <p className="text-xs font-bold text-slate-500">{step}</p>
            <p className="mt-2 text-xl font-bold text-brand-navy">{[8, 6, 4, 11][index]}</p>
          </div>
        ))}
      </div>
      <div className="mt-5 grid gap-3">
        {[
          ['김○○ 고객', '진료정보분석 첨부', '담당자 배정'],
          ['박○○ 고객', '화재보험설계 요청', '검토 중'],
          ['이○○ 고객', '보장분석 파일 등록', '완료'],
        ].map(([name, request, status]) => (
          <div key={name} className="rounded-md border border-slate-200 p-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-bold text-slate-950">{name}</p>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-500">{status}</span>
            </div>
            <p className="mt-2 text-sm font-medium text-slate-600">{request}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function CustomerTouchpointScreen() {
  return (
    <div className="min-h-[330px]">
      <div className="grid gap-4 sm:grid-cols-[0.72fr_1fr]">
        <div className="rounded-[28px] border border-slate-200 bg-slate-950 p-3 text-white">
          <div className="rounded-[22px] bg-white p-4 text-slate-950">
            <div className="h-16 rounded-lg bg-gradient-to-r from-brand-navy to-brand-sky" />
            <p className="mt-5 text-lg font-bold">설계EZ 플래너</p>
            <p className="mt-1 text-sm font-medium text-slate-500">보장분석 · 보험금 청구 상담</p>
            <div className="mt-5 grid gap-2">
              {['상담 신청', '명함 공유', '카카오 문의'].map((item) => (
                <div key={item} className="rounded-md bg-[#f5f8ff] px-3 py-2 text-sm font-bold text-brand-navy">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="grid gap-3">
          <div className="rounded-md border border-slate-200 p-4">
            <p className="text-xs font-bold text-brand-sky">상담 접수</p>
            <h3 className="mt-2 text-xl font-bold text-slate-950">새 문의 12건</h3>
            <p className="mt-2 text-sm font-medium leading-6 text-slate-600">
              랜딩페이지와 개인홈페이지에서 들어온 상담을 한곳에 모읍니다.
            </p>
          </div>
          {['모바일 명함 공유', '개인홈페이지 방문', '상담 신청 완료'].map((item) => (
            <div key={item} className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-3 text-sm font-bold text-slate-700">
              <CheckCircle2 className="h-4 w-4 text-brand-sky" aria-hidden />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function SgezLandingPage() {
  return (
    <div id="top" className="min-h-screen bg-white text-slate-950">
      <MarketingSiteHeader />

      <main>
        <section className="bg-white">
          <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 md:py-24 lg:min-h-[650px] lg:grid-cols-[0.82fr_1.18fr] lg:items-center">
            <div className="max-w-2xl">
              <p className="text-sm font-bold text-brand-sky">보험설계사를 위한 고객 접점 + 분석 + 업무관리 통합 플랫폼</p>
              <h1 className="mt-6 text-[2.35rem] font-bold leading-[1.14] tracking-normal text-slate-950 sm:text-[3rem] lg:text-[3.65rem]">
                보험설계 업무의
                <br />
                표준을 재정의하다.
              </h1>
              {/* <p className="mt-6 max-w-lg text-base font-medium leading-8 text-slate-700 sm:text-lg">
                고객을 만나는 순간부터 분석, 설계의뢰, 계약 후 관리까지
                <br />
                하나의 흐름으로 이어집니다.
              </p> */}
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <PrimaryLink to="/login">
                  시작하기
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </PrimaryLink>
                <OutlineLink to={PUBLIC_INQUIRY_PATH}>도입 상담</OutlineLink>
              </div>
            </div>

            <div className="relative flex items-center justify-center">
              <LandingFlowHeroImage className="w-full max-w-[720px] object-contain" />
            </div>
          </div>
          <div className="border-y border-slate-200">
            <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-4 py-6 text-sm font-bold text-slate-500 sm:grid-cols-5">
              {heroFlowItems.map((item) => (
                <div key={item} className="flex items-center justify-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-sky" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#eaf7ff] py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 lg:grid-cols-[0.9fr_1fr] lg:gap-16">
            <div className="order-2 lg:order-1">
              <img
                src="/sgez-landing-assets/sgez-workflow-wheel.svg"
                alt="영업관리, 고객관리, 설계관리, 업무관리로 이어지는 설계EZ 통합 업무 흐름"
                className="mx-auto block w-full max-w-[620px] object-contain"
                loading="lazy"
              />
            </div>

            <div className="order-1 lg:order-2">
              <div className="flex items-start gap-5">
                <div>
                  <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-950 sm:text-4xl lg:text-[3rem]">
                    업무의 시작부터 끝까지{' '}
                  </h2>
                  <p className="mt-7 max-w-2xl text-base font-medium leading-8 text-slate-700 sm:text-lg">
                    고객을 만나는 순간부터 분석과 설계의뢰, 계약 후 관리까지 끊김 없이
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white py-14 md:py-16" aria-labelledby="service-introduction-title">
          <div className="mx-auto max-w-7xl px-4">
            <div className="flex flex-col gap-6 rounded-lg border border-slate-200 bg-slate-50 p-7 sm:flex-row sm:items-center sm:justify-between md:p-9">
              <h2 id="service-introduction-title" className="text-2xl font-bold text-slate-950 sm:text-3xl">
                설계EZ 서비스소개서
              </h2>
              <a
                href="/downloads/sgez-service-introduction.pdf"
                download="설계EZ_서비스소개서.pdf"
                className="inline-flex min-h-[50px] items-center justify-center gap-2 rounded-md bg-brand-navy px-6 text-sm font-bold text-white shadow-lg shadow-blue-950/10 transition hover:bg-sky-700"
              >
                서비스소개서 다운로드
                <Download className="h-4 w-4" aria-hidden />
              </a>
            </div>
          </div>
        </section>

        <section className="bg-[#07111f] py-20 text-white md:py-28">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm font-bold text-white/70">주요 기능</p>
              <h2 className="mt-3 text-3xl font-bold leading-tight sm:text-4xl">
                보험설계 업무에 필요한 기능을{' '}
                <br className="hidden sm:block" />
                한곳에 모았습니다.
              </h2>
            </div>
            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {featureCards.map(({ title, desc, Icon }) => (
                <article key={title} className="rounded-lg bg-white p-7 text-slate-950 shadow-[0_28px_80px_-62px_rgba(2,6,23,0.7)]">
                  <div className="flex h-12 w-12 items-center justify-center rounded-md bg-slate-100 text-slate-950">
                    <Icon className="h-6 w-6" aria-hidden />
                  </div>
                  <h3 className="mt-8 text-2xl font-bold leading-snug">{title}</h3>
                  <p className="mt-4 text-sm font-medium leading-6 text-slate-600">{desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-20 md:py-28">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm font-bold text-brand-sky">특징과 장점</p>
              <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-950 sm:text-4xl">
                많은 설계사들이 설계EZ를 선택한 이유
              </h2>

            </div>
            <div className="mt-16 grid gap-20">
              {screenShowcases.map(({ no, title, desc, chips, Screen }, index) => (
                <article key={title} className="grid gap-12 lg:grid-cols-[0.8fr_1fr] lg:items-center lg:gap-16 xl:gap-20">
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <p className="text-sm font-bold text-brand-sky">{no}</p>
                    <h3 className="mt-4 text-3xl font-bold leading-tight text-slate-950">{title}</h3>
                    <p className="mt-4 max-w-lg text-base font-medium leading-7 text-slate-600">{desc}</p>
                    <div className="mt-6 flex flex-wrap gap-2">
                      {chips.map((chip) => (
                        <span key={chip} className="rounded-full bg-[#eef5ff] px-3 py-1.5 text-xs font-bold text-brand-navy">
                          {chip}
                        </span>
                      ))}
                    </div>
                  </div>
                  <MonitorFrame>
                    <Screen />
                  </MonitorFrame>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-20 md:py-28">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm font-bold text-slate-500">기대효과</p>
              <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-950 sm:text-4xl">
                매일 체감하는 변화{' '}
              </h2>
            </div>
            <div className="mt-14 grid gap-10 text-center md:grid-cols-2 lg:grid-cols-4 lg:gap-12">
              {effectStats.map(([value, title, desc]) => (
                <div key={title} className="px-3">
                  <p className="text-6xl font-bold tracking-normal text-slate-950 sm:text-7xl">{value}</p>
                  <h3 className="mt-6 text-xl font-bold text-slate-950">{title}</h3>
                  <p className="mx-auto mt-3 max-w-56 text-sm font-medium leading-6 text-slate-600">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-20 md:py-24">
          <div className="mx-auto max-w-7xl px-4">
            <div className="grid gap-8 rounded-lg bg-gradient-to-r from-brand-navy to-brand-sky p-8 text-white md:grid-cols-[1fr_auto] md:items-end md:p-10">
              <div>
                <ShieldCheck className="h-7 w-7 text-white" aria-hidden />
                <h2 className="mt-5 text-3xl font-bold leading-tight sm:text-4xl">
                  보험설계 업무,
                  <br />
                  설계EZ로 이어보세요.
                </h2>
                <ul className="mt-7 grid gap-3 text-sm font-semibold text-sky-50 sm:grid-cols-3">
                  {['고객 접점', '상담 분석', '업무 운영'].map((item) => (
                    <li key={item} className="flex gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-white" aria-hidden />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <PrimaryLink to={PUBLIC_INQUIRY_PATH} variant="blue">도입 문의</PrimaryLink>
                <OutlineLink to="/pricing" light>요금 보기</OutlineLink>
              </div>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  )
}
